"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight, Globe, Smartphone } from "@/components/icons";
import { buttonVariants } from "@/components/ui/button";
import { ROUTES } from "@/lib/constants";

const PRODUCTS = [
  {
    icon: Globe,
    title: "Residential Proxies",
    description:
      "Ethically sourced residential IPs with rotating and sticky sessions. The default choice for public-web collection and monitoring at scale.",
    href: ROUTES.productResidentialProxies,
    cta: "Explore Residential Proxies",
    pricingHref: ROUTES.pricingResidentialProxies,
  },
  {
    icon: Smartphone,
    title: "Mobile Proxies",
    description:
      "4G/5G mobile IPs billed per GB or per proxy, for workloads that need mobile-network context.",
    href: ROUTES.productMobileProxies,
    cta: "Explore Mobile Proxies",
    pricingHref: ROUTES.pricingMobileProxies,
  },
];

export default function ProductOptions() {
  const reduced = useReducedMotion() ?? false;

  return (
    <section
      aria-labelledby="product-options-heading"
      className="border-border py-section relative border-b"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-2xl">
          <p className="text-muted-foreground mb-3 text-xs font-semibold">Proxidize options</p>
          <h2
            id="product-options-heading"
            className="text-foreground mb-3 scroll-mt-40 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            Proxidize product options
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            Once you know the proxy type that fits, here&apos;s where to start.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {PRODUCTS.map((product) => {
            const Icon = product.icon;
            return (
              <motion.div
                key={product.title}
                initial={reduced ? false : { opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={scrollRevealViewport}
                transition={{ duration: 0.45 }}
                className="border-border bg-card flex flex-col rounded-2xl border p-6 sm:p-8"
              >
                <span
                  className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg"
                  style={{
                    background: "color-mix(in srgb, var(--brand-green) 10%, transparent)",
                    color: "var(--brand-green)",
                  }}
                >
                  <Icon size={18} aria-hidden="true" />
                </span>
                <h3 className="text-foreground mb-2 text-lg font-semibold">{product.title}</h3>
                <p className="text-muted-foreground mb-6 flex-1 text-sm leading-relaxed">
                  {product.description}
                </p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                  <Link
                    href={product.href}
                    className={buttonVariants({ variant: "outline", size: "lg" })}
                  >
                    {product.cta}
                    <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                  <Link
                    href={product.pricingHref}
                    className="text-muted-foreground hover:text-foreground text-sm underline underline-offset-4"
                  >
                    See pricing
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        <p className="text-muted-foreground mt-8 text-sm leading-relaxed">
          Comparing proxy providers?{" "}
          <Link
            href={ROUTES.alternatives}
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            See how Proxidize stacks up
          </Link>
          .
        </p>
      </div>
    </section>
  );
}
